import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { RegisterForMeetingRequestDTO } from './types/RegisterForMeetingRequestDTO';
import { MinProjectDTO } from '../subscription/types/MinProjectDTO';
import { RegisterForSurveyRequestDTO } from './types/RegisterForSurveyRequestDTO';
import { FormLayoutService } from 'src/app/shared/components/form-layout/form-layout.service';
import { PublicOnlineMeetingDTO } from './types/PublicOnlineMeetingDTO';
import { PublicMeetingDTO } from '../comment/types/PublicMeetingDTO';
import { SubscriptionEmailRequestDTO } from '../subscription/types/SubscriptionEmailRequestDTO';

@Injectable({
  providedIn: 'root',
})
export class RegistrationService {
  options: any;

  meetingInfo$: BehaviorSubject<PublicMeetingDTO | null> =
    new BehaviorSubject<PublicMeetingDTO | null>(null);

  onlineMeetingInfo$: BehaviorSubject<PublicOnlineMeetingDTO | null> =
    new BehaviorSubject<PublicOnlineMeetingDTO | null>(null);

  projectInfo$: BehaviorSubject<MinProjectDTO | null> = new BehaviorSubject<MinProjectDTO | null>(
    null
  );

  constructor(
    private http: HttpClient,
    private formLayoutService: FormLayoutService
  ) {
    this.http = http;
  }

  getInPersonMeetingInfo(meetingGuid: string): Observable<PublicMeetingDTO> {
    const headers = new HttpHeaders({
      accept: '*/*',
    });
    const options = { headers: headers };

    return this.http
      .get<PublicMeetingDTO>(`${environment.ApiUrl}/Meeting/GetMeeting/${meetingGuid}`, options)
      .pipe(
        tap({
          next: (response: PublicMeetingDTO) => {
            this.meetingInfo$.next(response);
            this.projectInfo$.next(response);
            const currentMeetingInfo = this.meetingInfo$.getValue();
            if (currentMeetingInfo && currentMeetingInfo.Settings) {
              this.formLayoutService.setFormLayoutSettings(currentMeetingInfo.Settings);
            } else {
              console.error('Meeting info or settings are missing');
            }
          },
          error: (error) => {
            console.error(error);
          },
        })
      );
  }

  getOnlineMeetingInfo(meetingGuid: string): Observable<PublicOnlineMeetingDTO> {
    const headers = new HttpHeaders({
      accept: '*/*',
    });
    const options = { headers: headers };

    return this.http
      .get<PublicOnlineMeetingDTO>(
        `${environment.ApiUrl}/Meeting/GetOnlineMeeting/${meetingGuid}`,
        options
      )
      .pipe(
        tap({
          next: (response: PublicOnlineMeetingDTO) => {
            this.onlineMeetingInfo$.next(response);
            this.projectInfo$.next(response);
            const currentMeetingInfo = this.onlineMeetingInfo$.getValue();
            if (currentMeetingInfo && currentMeetingInfo.Settings) {
              this.formLayoutService.setFormLayoutSettings(currentMeetingInfo.Settings);
            } else {
              console.error('Meeting info or settings are missing');
            }
          },
          error: (error) => {
            console.error(error);
          },
        })
      );
  }

  getProjectInfo(projectGuid: string): Observable<MinProjectDTO> {
    const headers = new HttpHeaders({
      accept: '*/*',
    });
    const options = { headers: headers };
    return this.http
      .get<MinProjectDTO>(`${environment.ApiUrl}/Project/GetProject/${projectGuid}`, options)
      .pipe(
        tap({
          next: (response: MinProjectDTO) => {
            this.projectInfo$.next(response);
            if (response.Settings) {
              this.formLayoutService.setFormLayoutSettings(response.Settings);
            }
          },
          error: (error) => {
            console.error(error);
          },
        })
      );
  }

  registerStakeholderForMeeting(registrant: RegisterForMeetingRequestDTO) {
    const headers = new HttpHeaders({
      accept: '*/*',
    });
    const options = { headers: headers };
    return this.http.post(`${environment.ApiUrl}/Meeting`, registrant, options).pipe(
      tap({
        error: (error) => {
          console.error(error);
        },
      })
    );
  }

  getAnonymousStakeholder(): Observable<{ StakeGuid: string }> {
    const headers = new HttpHeaders({
      accept: '*/*',
    });
    const options = { headers: headers };

    return this.http
      .get<{ StakeGuid: string }>(`${environment.ApiUrl}/Stakeholder/GetAnonStakeholder`, options)
      .pipe(
        tap({
          error: (error) => {
            console.error(error);
          },
        })
      );
  }
  registerForSurvey(registrant: RegisterForSurveyRequestDTO): Observable<string> {
    const headers = new HttpHeaders({
      accept: '*/*',
    });
    const options = { headers: headers };

    return this.http
      .get<string>(
        `${environment.ApiUrl}/Survey123/GetFormUrl/${registrant.s123Id}/${registrant.stakeGuid}/${registrant.projectGuid}`,
        options
      )
      .pipe(
        tap({
          error: (error) => {
            console.error(error);
          },
        })
      );
  }

  triggerConfirmationSubscriptionEmail(
    projectGuid: string,
    stakeGuid: string,
    isSubscribed: boolean = false
  ): Observable<void> {
    const headers = new HttpHeaders({
      accept: '*/*',
    });
    const options = { headers: headers };
    const payload: SubscriptionEmailRequestDTO = {
      ProjectGuid: projectGuid,
      StakeGuid: stakeGuid,
      Subscribed: isSubscribed === null || isSubscribed === undefined ? true : isSubscribed,
    };

    return this.http.post<void>(`${environment.ApiUrl}/Subscription`, payload, options).pipe(
      tap({
        error: (error) => {
          console.error('Error sending confirmation subscription email:', error);
        },
      })
    );
  }
}
